import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Select, Space, Typography, Empty, Spin, Alert, Switch, Divider, Modal, theme, Button, message } from 'antd';
import { ExclamationCircleOutlined, EyeOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { HiTemplate } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import dayjs from '../../../utils/dayjs';
import FormPreview from '../../clinicalForms/FormPreview.component';
import { mapFormResponseDtoToEntity } from '../../../utils/functions';
import { getMissingRequiredQuestions } from '../../clinicalForms/utils/formValidation';

const { Title, Text } = Typography;

// Convert camelCase state key to UPPER_SNAKE_CASE action type
const toActionType = (key) => key.replace(/[A-Z]/g, c => `_${c}`).toUpperCase();

const FormBasedEntityEdit = ({
    entityId,
    patientId,
    getDetails,
    updateEntity,
    listForms,
    getFormDetails,
    getFormResponse,
    createFormResponse,
    updateFormResponse,
    entityStateKey,
    entityUpdateKey,
    entityKey,
    entityType,
    entityLabel,
    formCategory,
    onSuccess,
    onSaveStateChange,
    onView,
    patientFullName,
    patientDateOfBirth,
    patientYearOfBirth,
    // Optional: Control which entity fields to manage
    manageEnabled = true, // Set to false if entity doesn't have Enabled field
    // Optional: Custom components to render in the status panel
    statusPanelComponents,
    // Optional: Control initial panel state (default: true = collapsed)
    initialPanelCollapsed = true,
    /** When true, entityId is FormResponse id; getDetails=getFormResponseDetails; entityStateKey=formResponseDetails; entityKey=formResponse */
    formResponseAsEntity = false,
}) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { token } = theme.useToken();
    
    const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(initialPanelCollapsed);

    const entityDetails = useSelector(state => state[entityStateKey]);
    const { loading: loadingDetails, [entityKey]: rawEntity } = entityDetails || {};

    const entity = React.useMemo(() => {
        if (!formResponseAsEntity) return rawEntity;
        return mapFormResponseDtoToEntity(rawEntity) || rawEntity;
    }, [formResponseAsEntity, rawEntity]);
    
    const entityUpdate = useSelector(state => state[entityUpdateKey]);
    const { loading: loadingUpdate, success: updateSuccess, error: errorUpdate } = entityUpdate || {};
    
    const formList = useSelector(state => state.formList);
    const { forms, loading: loadingForms } = formList || {};
    
    const formDetails = useSelector(state => state.formDetails);
    const { form: selectedFormDetails, loading: loadingFormDetails } = formDetails || {};
    
    const formResponseDetails = useSelector(state => state.formResponseDetails);
    const { formResponse: existingFormResponse, loading: loadingFormResponse } = formResponseDetails || {};
    
    const formResponseCreate = useSelector(state => state.formResponseCreate);
    const { loading: savingFormResponse, success: formResponseSuccess, formResponse: savedFormResponse, error: errorFormResponseCreate } = formResponseCreate || {};
    
    const formResponseUpdate = useSelector(state => state.formResponseUpdate);
    const { loading: updatingFormResponse, success: formResponseUpdateSuccess, error: errorFormResponseUpdate } = formResponseUpdate || {};

    const [selectedFormId, setSelectedFormId] = useState(null);
    const [formValues, setFormValues] = useState({});
    const [hasChanges, setHasChanges] = useState(false);
    const [originalFormId, setOriginalFormId] = useState(null);
    const [entityEnabled, setEntityEnabled] = useState(true);
    const [loadedFormResponseId, setLoadedFormResponseId] = useState(null);
    const [additionalEntityFields, setAdditionalEntityFields] = useState({});
    // Local cache of form details to prevent shared state overwrites from other components
    const [cachedFormDetails, setCachedFormDetails] = useState(null);

    // Reset stale Redux states on mount to prevent immediate save completion trigger
    useEffect(() => {
        dispatch({ type: 'FORM_RESPONSE_UPDATE_RESET' });
        dispatch({ type: 'FORM_RESPONSE_CREATE_RESET' });
        dispatch({ type: `${toActionType(entityUpdateKey)}_RESET` });
    }, [dispatch, entityUpdateKey]);

    useEffect(() => {
        if (entityId) {
            dispatch(getDetails(entityId));
        }
        // Use entityType to filter forms - capitalize first letter to match backend format
        const capitalizedEntityType = entityType.charAt(0).toUpperCase() + entityType.slice(1);
        dispatch(listForms({ entityType: capitalizedEntityType }));
    }, [entityId, dispatch, getDetails, listForms, entityType]);

    useEffect(() => {
        if (entity && entity.id === entityId) {
            if (manageEnabled) {
                setEntityEnabled(entity.enabled !== false);
            }
            
            // Update selectedFormId if entity.defaultFormId exists and is different from current
            if (entity.defaultFormId) {
                if (!selectedFormId || selectedFormId !== entity.defaultFormId) {
                    setSelectedFormId(entity.defaultFormId);
                    setOriginalFormId(entity.defaultFormId);
                }
            }
            
            if (entity.formResponseId && loadedFormResponseId !== entity.formResponseId) {
                setLoadedFormResponseId(entity.formResponseId);
                if (!formResponseAsEntity) {
                    dispatch(getFormResponse(entity.formResponseId));
                }
            }
        }
    }, [entity, entityId, selectedFormId, loadedFormResponseId, dispatch, getFormResponse, manageEnabled, formResponseAsEntity]);

    useEffect(() => {
        if (selectedFormId) {
            // Only fetch if not already cached for this form
            if (!cachedFormDetails || cachedFormDetails.id !== selectedFormId) {
                dispatch(getFormDetails(selectedFormId));
            }
        }
    }, [selectedFormId, cachedFormDetails, dispatch, getFormDetails]);

    // Cache form details when they match the selected form
    useEffect(() => {
        if (selectedFormDetails && selectedFormId && selectedFormDetails.id === selectedFormId) {
            setCachedFormDetails(selectedFormDetails);
        }
    }, [selectedFormDetails, selectedFormId]);

    // Clear cache when entityId changes
    useEffect(() => {
        setCachedFormDetails(null);
    }, [entityId]);
    
    // Use cached form details if available, otherwise fall back to Redux state
    const effectiveFormDetails = cachedFormDetails || selectedFormDetails;

    useEffect(() => {
        if (existingFormResponse && existingFormResponse.id === loadedFormResponseId) {
            try {
                const values = JSON.parse(existingFormResponse.responseJson || '{}');
                setFormValues(values);
            } catch (e) {
                console.error('Error parsing form response:', e);
                setFormValues({});
            }
        }
    }, [existingFormResponse, loadedFormResponseId]);

    const [saveCompleted, setSaveCompleted] = useState(false);

    useEffect(() => {
        if (formResponseUpdateSuccess || updateSuccess) {
            setHasChanges(false);
            setSaveCompleted(true); // Signal that save is complete
            if (entityId) {
                dispatch(getDetails(entityId));
            }
            onSuccess?.();
            dispatch({ type: 'FORM_RESPONSE_UPDATE_RESET' });
            dispatch({ type: `${toActionType(entityUpdateKey)}_RESET` });
        }
    }, [formResponseUpdateSuccess, updateSuccess, entityId, dispatch, getDetails, entityUpdateKey, onSuccess]);

    useEffect(() => {
        if (formResponseSuccess && savedFormResponse && entityId) {
            dispatch(updateEntity(entityId, {
                formResponseId: savedFormResponse.id
            }));
            dispatch({ type: 'FORM_RESPONSE_CREATE_RESET' });
        }
    }, [formResponseSuccess, savedFormResponse, entityId, dispatch, updateEntity]);

    // Reset error states after they're displayed
    useEffect(() => {
        if (errorUpdate || errorFormResponseCreate || errorFormResponseUpdate) {
            // Errors are handled in the Alert component's onClose, but also reset after a delay
            // to ensure they don't persist if component unmounts
            const timer = setTimeout(() => {
                if (errorUpdate) {
                    dispatch({ type: `${toActionType(entityUpdateKey)}_RESET` });
                }
                if (errorFormResponseCreate) {
                    dispatch({ type: 'FORM_RESPONSE_CREATE_RESET' });
                }
                if (errorFormResponseUpdate) {
                    dispatch({ type: 'FORM_RESPONSE_UPDATE_RESET' });
                }
            }, 5000); // Auto-reset after 5 seconds

            return () => clearTimeout(timer);
        }
    }, [errorUpdate, errorFormResponseCreate, errorFormResponseUpdate, dispatch, entityUpdateKey]);

    // Cleanup: Reset all states on unmount to prevent stale messages and stale form data
    useEffect(() => {
        return () => {
            dispatch({ type: 'FORM_DETAILS_RESET' });
            dispatch({ type: 'FORM_RESPONSE_UPDATE_RESET' });
            dispatch({ type: 'FORM_RESPONSE_CREATE_RESET' });
            dispatch({ type: `${toActionType(entityUpdateKey)}_RESET` });
        };
    }, [dispatch, entityUpdateKey]);

    const handleFormChange = (newFormId) => {
        if (originalFormId && newFormId !== originalFormId && Object.keys(formValues).length > 0) {
            Modal.confirm({
                title: t('Change Form?'),
                icon: <ExclamationCircleOutlined />,
                content: t('Changing the form will clear all existing data. Are you sure?'),
                okText: t('Yes, Change'),
                cancelText: t('Cancel'),
                onOk: () => {
                    setSelectedFormId(newFormId);
                    setFormValues({});
                    setHasChanges(true);
                }
            });
        } else {
            setSelectedFormId(newFormId);
            setHasChanges(true);
        }
    };

    const handleValuesChange = useCallback((values) => {
        setFormValues(values);
        setHasChanges(true);
        setSaveCompleted(false); // Reset saveCompleted when user makes changes
    }, []);

    // Use refs for rapidly-changing values so handleSave keeps a stable identity
    const formValuesRef = useRef(formValues);
    formValuesRef.current = formValues;
    const entityEnabledRef = useRef(entityEnabled);
    entityEnabledRef.current = entityEnabled;
    const additionalEntityFieldsRef = useRef(additionalEntityFields);
    additionalEntityFieldsRef.current = additionalEntityFields;
    const formSchemaRef = useRef(null);

    const handleSave = useCallback(() => {
        if (!selectedFormId || !entityId) return;

        const currentFormValues = formValuesRef.current;
        const currentEntityEnabled = entityEnabledRef.current;
        const currentAdditionalFields = additionalEntityFieldsRef.current;

        const missingRequired = getMissingRequiredQuestions(formSchemaRef.current, currentFormValues);
        if (missingRequired.length > 0) {
            message.error(t('Please complete required field(s): {{fields}}', { fields: missingRequired.join(', ') }));
            return;
        }

        if (entity?.formResponseId) {
            dispatch(updateFormResponse(entity.formResponseId, {
                formId: selectedFormId,
                responseJson: JSON.stringify(currentFormValues),
                status: 'completed'
            }));
        } else {
            dispatch(createFormResponse({
                formId: selectedFormId,
                patientId,
                entityType: entityType,
                entityId: entityId,
                responseJson: JSON.stringify(currentFormValues),
                status: 'completed'
            }));
        }
        
        const needsUpdate = 
            (manageEnabled && currentEntityEnabled !== entity?.enabled) || 
            selectedFormId !== entity?.defaultFormId ||
            Object.keys(currentAdditionalFields).length > 0;
            
        if (needsUpdate) {
            const updateData = {
                ...(selectedFormId !== entity?.defaultFormId ? { defaultFormId: selectedFormId } : {}),
                ...currentAdditionalFields
            };
            if (manageEnabled) {
                updateData.enabled = currentEntityEnabled;
            }
            dispatch(updateEntity(entityId, updateData));
            setAdditionalEntityFields({});
        }
    }, [selectedFormId, entityId, entity, patientId, entityType, manageEnabled, dispatch, updateEntity, createFormResponse, updateFormResponse]);

    const isSaving = loadingUpdate || savingFormResponse || updatingFormResponse;
    const errors = [errorUpdate, errorFormResponseCreate, errorFormResponseUpdate].filter(Boolean);

    useEffect(() => {
        if (onSaveStateChange) {
            onSaveStateChange({
                saving: isSaving,
                hasChanges: hasChanges && !!selectedFormId,
                saveDisabled: !selectedFormId,
                onSave: handleSave,
                saveCompleted // Signal when save is complete for parent navigation
            });
        }
    }, [isSaving, hasChanges, selectedFormId, handleSave, onSaveStateChange, saveCompleted]);

    const formSchema = React.useMemo(() => {
        if (!effectiveFormDetails?.schemaJson) return null;
        try {
            return typeof effectiveFormDetails.schemaJson === 'string'
                ? JSON.parse(effectiveFormDetails.schemaJson)
                : effectiveFormDetails.schemaJson;
        } catch (error) {
            console.error('Error parsing form schema:', error);
            return null;
        }
    }, [effectiveFormDetails]);
    formSchemaRef.current = formSchema;

    if (loadingDetails) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <Spin tip={t(`Loading ${entityLabel.toLowerCase()}...`)} />
            </div>
        );
    }

    const leftPanelWidth = leftPanelCollapsed ? 0 : '220px';

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                    icon={leftPanelCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                    onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
                    size="small"
                >
                    {leftPanelCollapsed ? t('Show Panel') : t('Hide Panel')}
                </Button>
                {onView && (
                    <Button
                        icon={<EyeOutlined />}
                        onClick={onView}
                        size="small"
                    >
                        {t('View')}
                    </Button>
                )}
            </div>

            {errors.length > 0 && (
                <Alert
                    type="error"
                    message={t('Error')}
                    description={errors.map((err, idx) => <div key={idx}>{err}</div>)}
                    closable
                    style={{ marginBottom: 16 }}
                    onClose={() => {
                        dispatch({ type: `${toActionType(entityUpdateKey)}_RESET` });
                        dispatch({ type: 'FORM_RESPONSE_CREATE_RESET' });
                        dispatch({ type: 'FORM_RESPONSE_UPDATE_RESET' });
                    }}
                />
            )}

            <div style={{ display: 'flex', gap: 24, flex: 1, overflow: 'hidden' }}>
                {!leftPanelCollapsed && (
                    <div style={{ 
                        width: leftPanelWidth,
                        minWidth: leftPanelWidth,
                        overflow: 'auto',
                        padding: '16px',
                        borderRight: `1px solid ${token.colorBorder}`,
                        backgroundColor: token.colorFillQuaternary,
                        borderRadius: token.borderRadius,
                        transition: 'width 0.3s ease'
                    }}>
                        <Space direction="vertical" style={{ width: '100%' }} size="large">
                            {manageEnabled && (
                                <>
                                    <div>
                                        <Title level={5} style={{ marginBottom: 12, fontSize: 14 }}>{t('Status')}</Title>
                                        <Space>
                                            <Switch 
                                                checked={entityEnabled}
                                                onChange={(checked) => {
                                                    setEntityEnabled(checked);
                                                    setHasChanges(true);
                                                }}
                                            />
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {entityEnabled ? t('Active') : t('Inactive')}
                                            </Text>
                                        </Space>
                                    </div>
                                </>
                            )}

                            {entity && (
                                <div>
                                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                        {entity.createdAt && (
                                            <div>
                                                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 2 }}>
                                                    {t('Created')}:
                                                </Text>
                                                <Text style={{ fontSize: 12, display: 'block' }}>
                                                    {dayjs(entity.createdAt).format('DD/MM/YYYY HH:mm')}
                                                    {entity.createdBy && ` ${t('by')} ${entity.createdBy}`}
                                                </Text>
                                            </div>
                                        )}
                                        {entity.updatedAt && (
                                            <div>
                                                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 2 }}>
                                                    {t('Updated')}:
                                                </Text>
                                                <Text style={{ fontSize: 12, display: 'block' }}>
                                                    {dayjs(entity.updatedAt).format('DD/MM/YYYY HH:mm')}
                                                    {entity.updatedBy && ` ${t('by')} ${entity.updatedBy}`}
                                                </Text>
                                            </div>
                                        )}
                                    </Space>
                                </div>
                            )}

                            {manageEnabled && <Divider style={{ margin: 0 }} />}

                            {statusPanelComponents && (
                                <>
                                    {React.isValidElement(statusPanelComponents) 
                                        ? statusPanelComponents
                                        : statusPanelComponents({ 
                                            entity, 
                                            entityId, 
                                            entityEnabled, 
                                            setEntityEnabled,
                                            setHasChanges,
                                            hasChanges,
                                            setAdditionalEntityFields,
                                            additionalEntityFields
                                        })}
                                    <Divider style={{ margin: 0 }} />
                                </>
                            )}

                            <div>
                                <Space style={{ marginBottom: 12 }}>
                                    <HiTemplate size={16} />
                                    <Title level={5} style={{ margin: 0, fontSize: 14 }}>{t('Form')}</Title>
                                </Space>
                                <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 11 }}>
                                    {t('Select a form')}
                                </Text>
                                <Select
                                    placeholder={t('Select a form')}
                                    style={{ width: '100%' }}
                                    value={selectedFormId}
                                    onChange={handleFormChange}
                                    loading={loadingForms}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={(forms || []).map(f => ({
                                        value: f.id,
                                        label: f.name || f.code
                                    }))}
                                />
                            </div>
                        </Space>
                    </div>
                )}

                <div style={{ flex: 1, overflow: 'auto', paddingRight: '8px' }}>
                    {!cachedFormDetails && loadingFormDetails ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                            <Spin tip={t('Loading form...')} />
                        </div>
                    ) : formSchema && effectiveFormDetails?.id === selectedFormId ? (
                        <FormPreview
                            schema={formSchema}
                            initialValues={formValues}
                            onValuesChange={handleValuesChange}
                            compact={false}
                            formName={effectiveFormDetails?.name}
                            showHeader={true}
                            patientFullName={patientFullName}
                            patientDateOfBirth={patientDateOfBirth}
                            patientYearOfBirth={patientYearOfBirth}
                            patientId={patientId}
                        />
                    ) : (
                        <Empty description={t('Select a form template to begin')} style={{ marginTop: '60px' }} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default FormBasedEntityEdit;
